import { Controller } from '@hotwired/stimulus'

const fileUploadMinSizeLimit = 0
const fileUploadMaxSizeLimit = 20000000

// Connects to data-controller="file-upload"
export default class extends Controller {
  static targets = [
    'error',
    'fileDropArea',
    'form',
    'input',
    'select',
    'uploadIcon',
    'uploadSpinner',
  ]

  declare readonly errorTarget: HTMLElement
  declare readonly fileDropAreaTarget: HTMLElement
  declare readonly formTarget: HTMLFormElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly selectTarget: HTMLElement
  declare readonly uploadIconTarget: HTMLElement
  declare readonly uploadSpinnerTarget: HTMLElement

  connect (): void {
    // Only add event listeners to the form associated with this controller instance
    this.formTarget.addEventListener('turbo:submit-start', this.toggleLoading)
    this.formTarget.addEventListener('turbo:submit-end', this.toggleLoading)
  }

  openFileDialog (): void {
    this.inputTarget.click()
  }

  handleDrop (e: DragEvent): void {
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/heic',
      'application/pdf',
    ]

    if (!e.dataTransfer) return

    const files = [...e.dataTransfer.files]

    // For drag and drop the file types are not enforced so we should check the type here explicitly
    if (files.every((f) => allowedTypes.includes(f.type))) {
      if (files.every((f) => (f.size < fileUploadMaxSizeLimit && f.size > fileUploadMinSizeLimit))) {
        this.errorTarget.innerHTML = ''
        this.inputTarget.files = e.dataTransfer.files
        this.formTarget.requestSubmit()
      } else {
        if (files.some((f) => (f.size >= fileUploadMaxSizeLimit))) {
          this.errorTarget.innerHTML = 'One or more files exceeds 20MB'
        } else {
          this.errorTarget.innerHTML = 'One or more files is empty (0 bytes)'
        }
      }
    } else {
      this.errorTarget.innerHTML =
        'Only Image/PNGs, Image/JPEGs, Image/HEICs, and PDFs are allowed.'
    }
  }

  handleSelect (): void {
    if (!this.inputTarget.files) return

    const files = [...this.inputTarget.files]

    if (files.every((f) => (f.size < fileUploadMaxSizeLimit && f.size > fileUploadMinSizeLimit))) {
      this.errorTarget.innerHTML = ''
      this.formTarget.requestSubmit()
    } else {
      if (files.some((f) => (f.size >= fileUploadMaxSizeLimit))) {
        this.errorTarget.innerHTML = 'One or more files exceeds 20MB'
      } else {
        this.errorTarget.innerHTML = 'One or more files is empty (0 bytes)'
      }
    }
  }

  toggleLoading = (_e: Event): void => {
    // No need to check dataset since we're now directly listening on the specific form
    this.uploadIconTarget.hidden = !this.uploadIconTarget.hidden
    this.uploadSpinnerTarget.hidden = !this.uploadSpinnerTarget.hidden
  }

  preventDefaults (e: Event): void {
    e.preventDefault()
    e.stopPropagation()
  }

  highlight (e: Event): void {
    const target = e.currentTarget as HTMLElement
    if (!target) return

    target.classList.remove('border-gray-300')
    target.classList.add('border-thatch-purple-secondary')
    target.querySelector('svg')!.classList.remove('text-gray-300')
    target.querySelector('svg')!.classList.add('text-thatch-purple-secondary')
  }

  unhighlight (e: Event): void {
    const target = e.currentTarget as HTMLElement
    if (!target) return

    target.classList.remove('border-thatch-purple-secondary')
    target.classList.add('border-gray-300')
    target
      .querySelector('svg')!
      .classList.remove('text-thatch-purple-secondary')
    target.querySelector('svg')!.classList.add('text-gray-300')
  }
}
